import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { slugify } from '@tuic/lib/src/helpers/slug';
import { logger } from '@tuic/logger';

import { client } from '../../generated/pcl/client.gen';
import { CacheTag } from '../../utils';

import type { Pcl } from '../..';
import type { Locale } from '@tuic/i18n';

export type ApiKitShip = Pcl.Ship & {
  slug: string | undefined;
};

/**
 * Fetches the ship details from the PCL API.
 */
export const getShipDetails = async (
  shipCode: string,
  locale: Locale,
): Promise<ApiKitShip | null> => {
  const { data, error } = await client.get<Pcl.Ship, unknown, false>({
    url: '/ships/{shipCode}',
    path: {
      shipCode,
    },
    query: {
      locale,
    },
  });

  if (error) {
    logger.error(
      `Could not fetch ship details. Ship: ${shipCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return null;
  }

  if (typeof data === 'undefined') {
    return null;
  }

  const ship = data;

  return {
    ...ship,
    slug: ship.name ? slugify(ship.name) : undefined,
  };
};

/**
 * Fetches the ship details from the PCL API.
 */
export const cachedGetShipDetails = (
  shipCode: string,
  locale: Locale,
): Promise<ApiKitShip | null> =>
  remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getShipDetails', locale, shipCode]),
    () => getShipDetails(shipCode, locale),
    {
      tags: [CacheTag.pcl, CacheTag.shipResource, CacheTag.getShipDetails],
      ttl: 600,
    },
  );
